/**
 * ExportButton Component
 * 
 * A glassmorphism-styled button for exporting table data.
 * Displays loading spinner during export and success confirmation.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Table2, CheckCircle } from 'lucide-react';

interface ExportButtonProps {
    /** Handler called when button is clicked */
    onExport: () => void;
    /** Whether export is in progress */
    isExporting: boolean;
    /** Whether export just completed successfully */
    exportSuccess: boolean;
    /** Whether the button should be visible */
    isVisible: boolean;
}

/**
 * Animated export button with glass effect.
 * Designed to appear on hover over a table.
 */
export const ExportButton: React.FC<ExportButtonProps> = ({
    onExport,
    isExporting,
    exportSuccess,
    isVisible,
}) => {
    const { t } = useTranslation("common");

    return (
        <div
            className={`
                absolute -top-3 right-4 z-20
                transform transition-all duration-300 ease-out
                ${isVisible
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 -translate-y-2 pointer-events-none'
                }
            `}
        >
            <button
                onClick={onExport}
                disabled={isExporting}
                className={`
                    flex items-center gap-2 px-4 py-2 rounded-xl
                    text-sm font-medium
                    backdrop-blur-xl
                    border border-white/20 dark:border-white/10
                    shadow-lg shadow-black/5 dark:shadow-black/20
                    transition-all duration-200 ease-out
                    focus:outline-none focus:ring-2 focus:ring-accent/50
                    ${exportSuccess
                        ? 'bg-accent/90 text-white'
                        : 'bg-white/80 dark:bg-slate-800/80 text-slate-700 dark:text-slate-200 hover:bg-white dark:hover:bg-slate-700 hover:shadow-xl hover:scale-105'
                    }
                    ${isExporting ? 'cursor-wait' : 'cursor-pointer'}
                `}
                title={t('exportToExcel')}
                aria-label={t('exportTableToExcel')}
            >
                {exportSuccess ? (
                    <>
                        <CheckCircle className="w-4 h-4" aria-hidden="true" />
                        <span>{t('exported')}</span>
                    </>
                ) : isExporting ? (
                    <>
                        <div
                            className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin"
                            aria-hidden="true"
                        />
                        <span>{t('exporting')}</span>
                    </>
                ) : (
                    <>
                        <Table2 className="w-4 h-4" aria-hidden="true" />
                        <span>Excel</span>
                        <Download className="w-3.5 h-3.5 opacity-60" aria-hidden="true" />
                    </>
                )}
            </button>
        </div>
    );
};
